import type { FC, ReactNode } from 'react'
import type { Job } from '@/types'

import Link from 'next/link'
import {
    ArrowRight,
    CircleCheck,
    ClipboardCheck,
    Play,
    RotateCcw,
    Undo2,
    UserRound,
    CircleX
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { useConfirm } from '@/components/kit/confirm'
import { JobStatusBadge } from '@/components/kit/badge'
import { buttonClasses } from '@/components/kit/button'
import { StatusStepper } from '@/components/jobs/status-stepper'
import { useCarWash } from '@/hooks/use-carwash'
import { useCarWashActions } from '@/hooks/use-carwash-actions'
import { plateLabel } from '@/lib/carwash/plate'

interface StatusSheetProps {
    jobId: string | null
    open: boolean
    onClose: () => void
    onAssign: (jobId: string) => void
    onComplete: (jobId: string) => void
}

type Option = {
    key: string
    label: string
    detail: string
    icon: ReactNode
    tone?: 'primary' | 'danger'
    onSelect: () => void
}

const OptionButton: FC<{ option: Option }> = ({ option }): ReactNode => {
    return (
        <button
            type='button'
            onClick={option.onSelect}
            className={cn(
                'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                option.tone === 'primary' && 'border-primary/30 bg-accent hover:brightness-[0.98]',
                option.tone === 'danger' && 'border-red-200 bg-white hover:bg-red-50',
                !option.tone && 'border-border bg-card hover:bg-slate-50'
            )}
        >
            <span
                className={cn(
                    'grid size-10 shrink-0 place-items-center rounded-lg [&_svg]:size-5',
                    option.tone === 'primary' && 'bg-primary text-primary-foreground',
                    option.tone === 'danger' && 'bg-red-50 text-red-600',
                    !option.tone && 'bg-slate-100 text-slate-700'
                )}
            >
                {option.icon}
            </span>

            <span className='min-w-0 flex-1'>
                <span className={cn('block text-[15px] font-semibold', option.tone === 'danger' ? 'text-red-700' : 'text-foreground')}>
                    {option.label}
                </span>

                <span className='block text-[13px] text-muted-foreground'>
                    {option.detail}
                </span>
            </span>

            <ArrowRight className='size-4 shrink-0 text-slate-400' aria-hidden='true' />
        </button>
    )
}

/* Every status change a vehicle can make from where it is now. */
export const StatusSheet: FC<StatusSheetProps> = ({
    jobId,
    open,
    onClose,
    onAssign,
    onComplete
}): ReactNode => {
    const { lookups } = useCarWash()
    const actions = useCarWashActions()
    const toast = useToast()
    const confirm = useConfirm()

    const job = jobId && lookups ? lookups.jobs.get(jobId) ?? null : null
    const staffName = job?.staffId ? lookups?.staff.get(job.staffId)?.name ?? 'Staff' : null

    const buildOptions = (current: Job): Option[] => {
        const plate = plateLabel(current.plate, `#${current.number}`)

        const cancel: Option = {
            key: 'cancel',
            label: 'Cancel this vehicle',
            detail: 'Removes it from the board. No bill is created.',
            icon: <CircleX />,
            tone: 'danger',
            onSelect: async () => {
                const ok = await confirm({
                    title: `Cancel ${plate}?`,
                    description: 'The vehicle leaves the board and any staff member on it becomes free. This cannot be undone.',
                    confirmLabel: 'Cancel vehicle',
                    cancelLabel: 'Keep it',
                    tone: 'danger'
                })

                if (ok && actions.cancelJob(current.id)) {
                    toast({ title: `${plate} was cancelled`, tone: 'info' })
                    onClose()
                }
            }
        }

        const changeStaff: Option = {
            key: 'staff',
            label: 'Change staff',
            detail: staffName ? `Hand over from ${staffName}` : 'Pick who works on it',
            icon: <UserRound />,
            onSelect: () => {
                onClose()
                onAssign(current.id)
            }
        }

        const complete: Option = {
            key: 'complete',
            label: 'Complete wash',
            detail: 'Stops the timer, frees the staff member and creates the bill.',
            icon: <CircleCheck />,
            tone: 'primary',
            onSelect: () => {
                onClose()
                onComplete(current.id)
            }
        }

        switch (current.status) {
            case 'waiting':
                return [
                    {
                        key: 'assign',
                        label: 'Assign staff',
                        detail: 'Choose who washes this vehicle.',
                        icon: <UserRound />,
                        tone: 'primary',
                        onSelect: () => {
                            onClose()
                            onAssign(current.id)
                        }
                    },
                    cancel
                ]
            case 'assigned':
                return [
                    {
                        key: 'start',
                        label: 'Start wash',
                        detail: 'Starts the live timer.',
                        icon: <Play />,
                        tone: 'primary',
                        onSelect: () => {
                            if (actions.startWash(current.id)) {
                                toast({ title: `Washing started for ${plate}` })
                                onClose()
                            }
                        }
                    },
                    changeStaff,
                    {
                        key: 'unassign',
                        label: 'Back to waiting',
                        detail: 'Frees the staff member and returns the vehicle to the queue.',
                        icon: <Undo2 />,
                        onSelect: () => {
                            if (actions.unassignStaff(current.id)) {
                                toast({ title: `${plate} is back in the queue`, tone: 'info' })
                                onClose()
                            }
                        }
                    },
                    cancel
                ]
            case 'washing':
                return [
                    {
                        key: 'qc',
                        label: 'Send to quality check',
                        detail: 'Final inspection before handover.',
                        icon: <ClipboardCheck />,
                        tone: 'primary',
                        onSelect: () => {
                            if (actions.sendToQualityCheck(current.id)) {
                                toast({ title: `${plate} is in quality check` })
                                onClose()
                            }
                        }
                    },
                    { ...complete, tone: undefined },
                    changeStaff,
                    cancel
                ]
            case 'quality_check':
                return [
                    complete,
                    {
                        key: 'rework',
                        label: 'Back to washing',
                        detail: 'Something needs another pass. The timer keeps running.',
                        icon: <RotateCcw />,
                        onSelect: () => {
                            if (actions.sendBackToWashing(current.id)) {
                                toast({ title: `${plate} went back to washing`, tone: 'info' })
                                onClose()
                            }
                        }
                    },
                    changeStaff,
                    cancel
                ]
            default:
                return []
        }
    }

    const options = job ? buildOptions(job) : []

    return (
        <Sheet
            open={open}
            onClose={onClose}
            title='Update status'
            description={job ? `${plateLabel(job.plate, `Vehicle #${job.number}`)}, ${job.serviceName}` : undefined}
        >
            {job && (
                <div className='space-y-5'>
                    <div className='rounded-xl border border-border bg-surface p-4'>
                        <div className='mb-4 flex items-center justify-between'>
                            <span className='text-[13px] font-semibold text-muted-foreground'>
                                Current stage
                            </span>

                            <JobStatusBadge status={job.status} />
                        </div>

                        <StatusStepper job={job} orientation='vertical' />
                    </div>

                    {options.length > 0 && (
                        <div className='space-y-2'>
                            {options.map(option => <OptionButton key={option.key} option={option} />)}
                        </div>
                    )}

                    {job.status === 'completed' && job.invoiceId && (
                        <Link href={`/billing/${job.invoiceId}`} onClick={onClose} className={buttonClasses({ variant: 'secondary', size: 'lg', className: 'w-full' })}>
                            View bill
                        </Link>
                    )}

                    {job.status === 'cancelled' && (
                        <p className='text-sm text-muted-foreground'>
                            This vehicle was cancelled and is no longer on the board.
                        </p>
                    )}
                </div>
            )}
        </Sheet>
    )
}
